import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import RequireAuth from "@/components/RequireAuth";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import GovLoginPage from "@/pages/gov/GovLoginPage";
import GovShell from "@/pages/gov/GovShell";
import GovDashboard from "@/pages/gov/GovDashboard";
import GovKnowledgeBase from "@/pages/gov/GovKnowledgeBase";
import GovTemplates from "@/pages/gov/GovTemplates";
import GovComingSoon from "@/pages/gov/GovComingSoon";
import GovCidadaos from "@/pages/gov/GovCidadaos";
import GovConversas from "@/pages/gov/GovConversas";
import GovCampanhas from "@/pages/gov/GovCampanhas";
import GovIntegracoes from "@/pages/gov/GovIntegracoes";
import GovOperacao from "@/pages/gov/GovOperacao";
import GovSecretarias from "@/pages/gov/GovSecretarias";
import GovLGPD from "@/pages/gov/GovLGPD";
import GovSuporte from "@/pages/gov/GovSuporte";
import GovPendentes from "@/pages/gov/GovPendentes";
import GovAvaliacoes from "@/pages/gov/GovAvaliacoes";
import GovProtocolos from "@/pages/gov/GovProtocolos";
import GovConfiguracoes from "@/pages/gov/GovConfiguracoes";
import Dashboard from "@/pages/Dashboard";
import ImportData from "@/pages/ImportData";
import Customers from "@/pages/Customers";
import Vehicles from "@/pages/Vehicles";
import Events from "@/pages/Events";
import Conversations from "@/pages/Conversations";
import Appointments from "@/pages/Appointments";
import Actions from "@/pages/Actions";
import MessageSettings from "@/pages/MessageSettings";
import Intelligence from "@/pages/Intelligence";
import Integrations from "@/pages/Integrations";
import Developer from "@/pages/Developer";
import Developers from "@/pages/Developers";
import ROI from "@/pages/ROI";
import Decision from "@/pages/Decision";
import Campaigns from "@/pages/Campaigns";
import RecurringCampaigns from "@/pages/RecurringCampaigns";
import Financial from "@/pages/Financial";
import Settings from "@/pages/Settings";
import PostService from "@/pages/PostService";
import PrivacyPanel from "@/pages/PrivacyPanel";
// v0.96.30 — Budgets page (dormant/hidden per user decision — code kept for
// future enterprise verticals like architects/lawyers). Rota e link do
// sidebar removidos; backend segue intacto pra ser reativado em segundos.
// import Budgets from "@/pages/Budgets";
import Reviews from "@/pages/Reviews";
import PublicReview from "@/pages/PublicReview";
import Team from "@/pages/Team";
import AuditTimeline from "@/pages/AuditTimeline";
import AcceptInvite from "@/pages/AcceptInvite";
import LegalTerms from "@/pages/LegalTerms";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Regularize from "@/pages/Regularize";

import RepLinkDispatcher from "@/pages/RepLinkDispatcher";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminTenantDetail from "@/pages/admin/AdminTenantDetail";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminAlertSettings from "@/pages/admin/AdminAlertSettings";
import AdminInfra from "@/pages/admin/AdminInfra";
import AdminMargens from "@/pages/admin/AdminMargens";
import AdminAccounts from "@/pages/admin/AdminAccounts";
import AdminGov from "@/pages/admin/AdminGov";
import AdminAIObservability from "@/pages/admin/AdminAIObservability";
import AdminRepresentatives from "@/pages/admin/AdminRepresentatives";
import AdminRepresentativeDetail from "@/pages/admin/AdminRepresentativeDetail";
import AdminRepMaterials from "@/pages/admin/AdminRepMaterials";
import AdminFiscalInvoices from "@/pages/admin/AdminFiscalInvoices";
import RepLogin from "@/pages/rep/RepLogin";
import RepShell from "@/pages/rep/RepShell";
import RepDashboard from "@/pages/rep/RepDashboard";
import RepClients from "@/pages/rep/RepClients";
import RepCommissions from "@/pages/rep/RepCommissions";
import RepMaterials from "@/pages/rep/RepMaterials";
import RepAccount from "@/pages/rep/RepAccount";
import RepAcceptTerms from "@/pages/rep/RepAcceptTerms";
import Operacao from "@/pages/Operacao";
import PendingQuestions from "@/pages/PendingQuestions";
import TestMode from "@/pages/TestMode";
import DemoAutoLogin from "@/pages/DemoAutoLogin";
import DemoSimulator from "@/pages/DemoSimulator";
// v0.99.46 · Fev/2026 — Mandato & Governo
import MandateRepresentatives from "@/pages/mandate/MandateRepresentatives";
import MandateKnowledge from "@/pages/mandate/MandateKnowledge";
import MandateDemands from "@/pages/mandate/MandateDemands";
import MandateAgenda from "@/pages/mandate/MandateAgenda";

import { LiveCountsProvider } from "@/contexts/LiveCountsContext";
import { SectorProvider } from "@/lib/sectorContext";

function AppLayout() {
  return (
    <RequireAuth>
      <SectorProvider>
        <LiveCountsProvider>
          <AppShell />
        </LiveCountsProvider>
      </SectorProvider>
    </RequireAuth>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auto-login" element={<DemoAutoLogin />} />
          <Route path="/demo-loading" element={<DemoAutoLogin />} />
          <Route path="/demo/simulator" element={<DemoSimulator />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cadastro" element={<Register />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/sucesso" element={<CheckoutSuccess />} />
          <Route path="/regularizar" element={<Regularize />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/aceitar-convite" element={<AcceptInvite />} />
          {/* v0.99.2 · Fev/2026 — Portal público de documentação da API */}
          <Route path="/desenvolvedores" element={<Developers />} />
          <Route path="/developers" element={<Developers />} />
          {/* v0.98 · Vertical GOV · Fev/2026 — rota paralela ao mercado privado */}
          <Route path="/gov" element={<GovLoginPage />} />
          <Route path="/gov/login" element={<GovLoginPage />} />
          {/* v0.98 SPRINT 2 · Fev/2026 — Portal institucional autenticado */}
          <Route path="/gov/painel" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovDashboard />} />
          </Route>
          <Route path="/gov/base-conhecimento" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovKnowledgeBase />} />
          </Route>
          <Route path="/gov/modelos" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovTemplates />} />
          </Route>
          {/* v0.99.8e · Fev/2026 — Placeholders "em breve" pros itens do
              menu institucional que ainda não têm implementação completa.
              Mantém o menu do GovShell coerente com a expectativa do
              operador (Conversas, Cidadãos, Campanhas, WhatsApp, Config). */}
          <Route path="/gov/conversas" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovConversas />} />
          </Route>
          <Route path="/gov/cidadaos" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovCidadaos />} />
          </Route>
          <Route path="/gov/campanhas" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovCampanhas />} />
          </Route>
          <Route path="/gov/integracoes" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovIntegracoes />} />
          </Route>
          <Route path="/gov/operacao" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovOperacao />} />
          </Route>
          <Route path="/gov/secretarias" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovSecretarias />} />
          </Route>
          <Route path="/gov/lgpd" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovLGPD />} />
          </Route>
          <Route path="/gov/suporte" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovSuporte />} />
          </Route>
          <Route path="/gov/pendentes" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovPendentes />} />
          </Route>
          <Route path="/gov/avaliacoes" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovAvaliacoes />} />
          </Route>
          <Route path="/gov/protocolos" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovProtocolos />} />
          </Route>
          <Route path="/gov/configuracoes" element={
            <RequireAuth><SectorProvider><GovShell /></SectorProvider></RequireAuth>
          }>
            <Route index element={<GovConfiguracoes />} />
          </Route>
          <Route path="/legal/termos" element={<LegalTerms />} />
          <Route path="/r/:token" element={<RepLinkDispatcher />} />
          <Route path="/app" element={<AppLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="importar" element={<ImportData />} />
            <Route path="clientes" element={<Customers />} />
            <Route path="veiculos" element={<Vehicles />} />
            <Route path="servicos" element={<Navigate to="/app/operacao#services" replace />} />
            <Route path="regras" element={<Navigate to="/app/decisao" replace />} />
            <Route path="eventos" element={<Events />} />
            <Route path="conversas" element={<Conversations />} />
            <Route path="conversas/:id" element={<Conversations />} />
            <Route path="agendamentos" element={<Appointments />} />
            <Route path="acoes" element={<Actions />} />
            <Route path="conhecimento" element={<Navigate to="/app/operacao#knowledge" replace />} />
            <Route path="mensagens" element={<Navigate to="/app/operacao#messages" replace />} />
            <Route path="mensagens/boas-vindas" element={<Navigate to="/app/operacao#messages" replace />} />
            <Route path="mensagens/automaticas" element={<MessageSettings />} />
            <Route path="inteligencia" element={<Intelligence />} />
            <Route path="recepcao" element={<Navigate to="/app/operacao#identity" replace />} />
            <Route path="modo-operacao" element={<Navigate to="/app/modo-teste" replace />} />
            <Route path="modo-teste" element={<TestMode />} />
            <Route path="decisao" element={<Decision />} />
            <Route path="campanhas" element={<Campaigns />} />
            <Route path="campanhas/recorrentes" element={<RecurringCampaigns />} />
            <Route path="roi" element={<ROI />} />
            <Route path="integracoes" element={<Integrations />} />
            <Route path="desenvolvedor" element={<Developer />} />
            <Route path="financeiro" element={<Financial />} />
            <Route path="minha-conta" element={<Settings />} />
            <Route path="privacidade" element={<PrivacyPanel />} />
            <Route path="ofertas" element={<Navigate to="/app/operacao#promotions" replace />} />
            <Route path="pos-atendimento" element={<PostService />} />
            <Route path="avaliacoes" element={<Reviews />} />
            <Route path="equipe" element={<Team />} />
            <Route path="auditoria" element={<AuditTimeline />} />
            <Route path="operacao" element={<Operacao />} />
            <Route path="perguntas-pendentes" element={<PendingQuestions />} />
            {/* v0.99.46 · Mandato & Governo */}
            <Route path="mandato/representantes" element={<MandateRepresentatives />} />
            <Route path="mandato/representantes/:mandateId/conhecimento" element={<MandateKnowledge />} />
            <Route path="mandato/demandas" element={<MandateDemands />} />
            <Route path="mandato/agenda" element={<MandateAgenda />} />
          </Route>
          <Route path="/admin-master/login" element={<AdminLogin />} />
          <Route path="/admin-master" element={<AdminShell />}>
            <Route index element={<AdminDashboard />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="tenants/:id" element={<AdminTenantDetail />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="infra" element={<AdminInfra />} />
            <Route path="margens" element={<AdminMargens />} />
            <Route path="ai-observability" element={<AdminAIObservability />} />
            <Route path="alert-settings" element={<AdminAlertSettings />} />
            <Route path="contas" element={<AdminAccounts />} />
            <Route path="gov" element={<AdminGov />} />
            <Route path="representantes" element={<AdminRepresentatives />} />
            <Route path="representantes/:id" element={<AdminRepresentativeDetail />} />
            <Route path="materiais-representantes" element={<AdminRepMaterials />} />
            <Route path="notas-fiscais" element={<AdminFiscalInvoices />} />
          </Route>
          <Route path="/representante/login" element={<RepLogin />} />
          <Route path="/representante/aceitar-termos" element={<RepAcceptTerms />} />
          <Route path="/representante" element={<RepShell />}>
            <Route index element={<RepDashboard />} />
            <Route path="clientes" element={<RepClients />} />
            <Route path="comissoes" element={<RepCommissions />} />
            <Route path="materiais" element={<RepMaterials />} />
            <Route path="conta" element={<RepAccount />} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
